import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useGoogleLogin } from '../hooks/useGoogleLogin';
import { useAuth } from '../hooks/AuthContext';
import { api } from '../hooks/useApi';
import { 
  Eye, 
  EyeOff, 
  Mail, 
  Lock, 
  ArrowRight, 
  User
} from 'lucide-react';
import { Link } from 'react-router-dom';
import EmailVerificationModal from './EmailVerificationModal';

type AuthView = 'signin' | 'signup' | 'forgot' | 'reset';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialView?: AuthView;
}

export default function AuthModal({ onClose, initialView = 'signin' }: AuthModalProps) {
  const navigate = useNavigate();
  const { isAuthenticated, login } = useAuth();
  const { handleGoogleLogin, isLoading: isGoogleLoading, error: googleError } = useGoogleLogin();
  
  const [currentView, setCurrentView] = useState<AuthView>(initialView);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: localStorage.getItem('rememberedEmail') || '',
    password: '',
    confirmPassword: '',
  });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [rememberMe, setRememberMe] = useState(!!localStorage.getItem('rememberedEmail'));
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [verificationEmail] = useState('');

  // Redirect if already authenticated
  if (isAuthenticated) {
    navigate('/');
    return null;
  }

  const onGoogleClick = async () => {
    try {
      const result = await handleGoogleLogin();
      if (result) {
        navigate('/profile');
        onClose();
      }
    } catch {
      // Error handled in hook
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox' && name === 'agreedToTerms') {
      setAgreedToTerms(checked);
    } else if (type === 'checkbox' && name === 'rememberMe') {
      setRememberMe(checked);
    } else {
      setFormData({ ...formData, [name]: value });
    }
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateSignup = () => {
    const newErrors: {
      firstName?: string;
      lastName?: string;
      email?: string;
      password?: string;
      confirmPassword?: string;
      agreedToTerms?: string;
    } = {};

    // First Name Validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters';
    } else if (formData.firstName.trim().length > 50) {
      newErrors.firstName = 'First name must be less than 50 characters';
    } else if (!/^[a-zA-Z\s'-]+$/.test(formData.firstName.trim())) {
      newErrors.firstName = 'First name can only contain letters, spaces, hyphens, and apostrophes';
    } else if (/^[-']|[ -']$/.test(formData.firstName.trim())) {
      newErrors.firstName = 'First name cannot start or end with special characters';
    } else if (formData.firstName.trim().split(/\s+/).length > 2) {
      newErrors.firstName = 'First name cannot contain more than 2 words';
    }

    // Last Name Validation
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    } else if (formData.lastName.trim().length < 2) {
      newErrors.lastName = 'Last name must be at least 2 characters';
    } else if (formData.lastName.trim().length > 50) {
      newErrors.lastName = 'Last name must be less than 50 characters';
    } else if (!/^[a-zA-Z\s'-]+$/.test(formData.lastName.trim())) {
      newErrors.lastName = 'Last name can only contain letters, spaces, hyphens, and apostrophes';
    } else if (/^[-']|[ -']$/.test(formData.lastName.trim())) {
      newErrors.lastName = 'Last name cannot start or end with special characters';
    } else if (formData.lastName.trim().split(/\s+/).length > 2) {
      newErrors.lastName = 'Last name cannot contain more than 2 words';
    }

    // Email Validation
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    } else if (formData.email.length > 254) {
      newErrors.email = 'Email address is too long';
    }

    // Password Validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (formData.password.length > 128) {
      newErrors.password = 'Password must be less than 128 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character';
    } else if (/^[a-zA-Z]+$|^[0-9]+$|^[^a-zA-Z0-9]+$/.test(formData.password)) {
      newErrors.password = 'Password must contain a mix of letters, numbers, and special characters';
    }

    // Confirm Password Validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Terms Validation
    if (!agreedToTerms) {
      newErrors.agreedToTerms = 'You must agree to Terms of Service and Privacy Policy';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateSignin = () => {
    const newErrors: {
      email?: string;
      password?: string;
    } = {};

    // Email Validation
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    } else if (formData.email.length > 254) {
      newErrors.email = 'Email address is too long';
    }

    // Password Validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 1) {
      newErrors.password = 'Password cannot be empty';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateForgot = () => {
    const newErrors: {
      email?: string;
    } = {};

    // Email Validation
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    } else if (formData.email.length > 254) {
      newErrors.email = 'Email address is too long';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validate = () => {
    switch (currentView) {
      case 'signup':
        return validateSignup();
      case 'signin':
        return validateSignin();
      case 'forgot':
        return validateForgot();
      default:
        return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    
    setIsLoading(true);
    
    try {
      if (currentView === 'signup') {
        const payload = {
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          password: formData.password,
          confirm_password: formData.confirmPassword,
        };
        
        const response = await api.post<Record<string, string>>('/api/auth/register/', payload);
        
        toast.success(response.message);

        setCurrentView('signin');
      
      } else if (currentView === 'signin') {
        const payload = {
          email: formData.email,
          password: formData.password,
        };
        
        const response = await api.post<{
          data?: {
            user?: {
              full_name: string;
              email: string;
              is_verified: boolean;
            };
            tokens?: {
              access: string;
              refresh: string;
            };
          };
        }>('/api/auth/login/', payload);
        
        if (response.data?.tokens?.access) {
          localStorage.setItem('token', response.data.tokens.access);
        }
        if (response.data?.tokens?.refresh) {
          localStorage.setItem('refreshToken', response.data.tokens.refresh);
        }
        if (response.data?.user) {
          localStorage.setItem('user', JSON.stringify(response.data.user));
          // Update auth state immediately
          login(response.data.user, response.data.tokens?.access || '', response.data.tokens?.refresh);
        }
        
        // Save email if remember me is checked
        if (rememberMe) {
          localStorage.setItem('rememberedEmail', formData.email);
        } else {
          localStorage.removeItem('rememberedEmail');
        }
        
        toast.success('Welcome back! You are now signed in.');
        
        await new Promise(resolve => setTimeout(resolve, 300));
        onClose();
      } else if (currentView === 'forgot') {
        const payload = {
          email: formData.email,
        };
        
        await api.post('/api/auth/password/reset/', payload);
        
        toast.success('Password reset instructions sent to your email.');
        
        await new Promise(resolve => setTimeout(resolve, 300));
        setCurrentView('signin');
      }
    } catch (error: any) {
      console.error('Auth error:', error);

      // Extract a clean, user-friendly error message
      const apiErrors = error?.response?.data;
      const fromArray = (val: any) => (Array.isArray(val) ? val[0] : val);
      const stripPrefix = (msg: string) =>
        typeof msg === 'string' ? msg.replace(/^\s*[^:]+:\s*/, '').trim() : msg;

      if (apiErrors) {
        let message: string | undefined;

        // Prefer nested `data.non_field_errors`
        if (apiErrors.data?.non_field_errors) {
          message = fromArray(apiErrors.data.non_field_errors);
        } else if (apiErrors.non_field_errors) {
          message = fromArray(apiErrors.non_field_errors);
        } else if (apiErrors.data) {
          // Look for the first field error inside `data`
          const firstKey = Object.keys(apiErrors.data)[0];
          if (firstKey) message = fromArray(apiErrors.data[firstKey]);
        }

        // Fall back to top-level specific fields
        if (!message && apiErrors.email) message = fromArray(apiErrors.email);
        if (!message && apiErrors.password) message = fromArray(apiErrors.password);
        if (!message && apiErrors.detail) message = apiErrors.detail;
        if (!message && apiErrors.message) message = apiErrors.message;

        toast.error(stripPrefix(message || 'Something went wrong. Please try again.'));
      } else if (error?.code === 'ERR_NETWORK') {
        toast.error('Network error. Please check your connection.');
      } else {
        toast.error('Something went wrong. Please try again.');
      }

      // Format errors for form display
      if (apiErrors) {
        const formattedErrors: Record<string, string> = {};

        // Prefer nested `data` object for field-level messages
        const source = typeof apiErrors.data === 'object' && apiErrors.data ? apiErrors.data : apiErrors;
        Object.keys(source).forEach((key) => {
          const val = source[key];
          if (key === 'non_field_errors') {
            formattedErrors.general = fromArray(val);
          } else if (Array.isArray(val)) {
            formattedErrors[key] = val[0];
          } else if (typeof val === 'string') {
            formattedErrors[key] = val;
          }
        });

        // Fallback to top-level message when no fields present
        if (Object.keys(formattedErrors).length === 0 && apiErrors.message) {
          formattedErrors.general = stripPrefix(apiErrors.message);
        }

        setErrors(formattedErrors);
      } else {
        setErrors({ general: 'An error occurred. Please try again.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const switchView = (view: AuthView) => {
    setCurrentView(view);
    setErrors({});
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
    });
    setAgreedToTerms(false);
  };

  const renderContent = () => {
    switch (currentView) {
      case 'signup':
        return (
          <div className="space-y-4">
            {/* Header */}
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-primary-100 mb-3">
                <User className="w-5 h-5 text-primary-600" />
              </div>
              <h2 className="text-xl font-bold text-dark-900 mb-1">Create Your Account</h2>
              <p className="text-sm text-dark-500">Start buying and selling today</p>
            </div>

            {/* Google Sign Up */}
            <button
              type="button"
              onClick={onGoogleClick}
              disabled={isGoogleLoading || isLoading}
              className="w-full flex items-center justify-center gap-3 px-4 py-2.5 border border-dark-200 rounded-lg hover:border-dark-300 hover:bg-dark-50 transition-all duration-200 font-semibold text-dark-700 disabled:opacity-70"
            >
              {isGoogleLoading ? (
                <div className="w-4 h-4 border-2 border-dark-300 border-t-primary-500 rounded-full animate-spin" />
              ) : (
                <>
                  <svg className="w-4 h-4" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Sign up with Google
                </>
              )}
            </button>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-dark-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-3 bg-white text-dark-400">Or use your email</span>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-3">
              {/* Names */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-dark-700 mb-1">First Name</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className={`h-4 w-4 ${errors.firstName ? 'text-danger' : 'text-dark-400'}`} />
                    </div>
                    <input
                      name="firstName"
                      type="text"
                      value={formData.firstName}
                      onChange={handleChange}
                      className={`block w-full pl-9 pr-3 py-2 border ${
                        errors.firstName ? 'border-danger' : 'border-dark-200'
                      } rounded-lg text-dark-900 placeholder-dark-400 focus:outline-none focus:border-primary-500 text-sm`}
                      placeholder="e.g. John"
                    />
                  </div>
                  {errors.firstName && (
                    <p className="mt-1 text-xs text-danger">{errors.firstName}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-dark-700 mb-1">Last Name</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className={`h-4 w-4 ${errors.lastName ? 'text-danger' : 'text-dark-400'}`} />
                    </div>
                    <input
                      name="lastName"
                      type="text"
                      value={formData.lastName}
                      onChange={handleChange}
                      className={`block w-full pl-9 pr-3 py-2 border ${
                        errors.lastName ? 'border-danger' : 'border-dark-200'
                      } rounded-lg text-dark-900 placeholder-dark-400 focus:outline-none focus:border-primary-500 text-sm`}
                      placeholder="e.g. Doe"
                    />
                  </div>
                  {errors.lastName && (
                    <p className="mt-1 text-xs text-danger">{errors.lastName}</p>
                  )}
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-dark-700 mb-1">Email</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className={`h-4 w-4 ${errors.email ? 'text-danger' : 'text-dark-400'}`} />
                  </div>
                  <input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`block w-full pl-9 pr-3 py-2 border ${
                      errors.email ? 'border-danger' : 'border-dark-200'
                    } rounded-lg text-dark-900 placeholder-dark-400 focus:outline-none focus:border-primary-500 text-sm`}
                    placeholder="you@example.com"
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-xs text-danger">{errors.email}</p>
                )}
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-semibold text-dark-700 mb-1">Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className={`h-4 w-4 ${errors.password ? 'text-danger' : 'text-dark-400'}`} />
                  </div>
                  <input
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    className={`block w-full pl-9 pr-8 py-2 border ${
                      errors.password ? 'border-danger' : 'border-dark-200'
                    } rounded-lg text-dark-900 placeholder-dark-400 focus:outline-none focus:border-primary-500 text-sm`}
                    placeholder="•••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-dark-400 hover:text-dark-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-xs text-danger">{errors.password}</p>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-semibold text-dark-700 mb-1">Confirm Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className={`h-4 w-4 ${errors.confirmPassword ? 'text-danger' : 'text-dark-400'}`} />
                  </div>
                  <input
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`block w-full pl-9 pr-8 py-2 border ${
                      errors.confirmPassword ? 'border-danger' : 'border-dark-200'
                    } rounded-lg text-dark-900 placeholder-dark-400 focus:outline-none focus:border-primary-500 text-sm`}
                    placeholder="•••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-dark-400 hover:text-dark-600"
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="mt-1 text-xs text-danger">{errors.confirmPassword}</p>
                )}
              </div>

              {/* Terms */}
              <div>
                <div className="flex items-start gap-2">
                  <input
                    id="agreedToTerms"
                    name="agreedToTerms"
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={handleChange}
                    className={`h-4 w-4 text-primary-600 focus:ring-primary-500 ${errors.agreedToTerms ? 'border-danger' : 'border-dark-300'} rounded mt-0.5`}
                  />
                  <label htmlFor="agreedToTerms" className="text-xs text-dark-600">
                    I agree to the{' '}
                    <Link to="/terms" className="text-primary-600 hover:text-primary-700 font-semibold">Terms of Service</Link>
                    {' '}and{' '}
                    <Link to="/privacy" className="text-primary-600 hover:text-primary-700 font-semibold">Privacy Policy</Link>
                  </label>
                </div>
                {errors.agreedToTerms && (
                  <p className="mt-1 text-xs text-danger">{errors.agreedToTerms}</p>
                )}
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isLoading || !agreedToTerms}
                className="w-full flex items-center justify-center gap-2 bg-primary-600 text-white px-4 py-2.5 rounded-lg font-semibold hover:bg-primary-700 disabled:opacity-70 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Creating your account...
                  </>
                ) : (
                  <>
                    Create Account
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            {/* Footer */}
            <div className="text-center pt-2">
              <p className="text-sm text-dark-500">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => switchView('signin')}
                  className="text-primary-600 hover:text-primary-700 font-semibold"
                >
                  Sign in here
                </button>
              </p>
            </div>
          </div>
        );

      case 'signin':
        return (
          <div>
            {/* Header */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary-100 mb-4">
                <Lock className="w-6 h-6 text-primary-600" />
              </div>
              <h2 className="text-2xl font-bold text-dark-900 mb-2">Welcome Back</h2>
              <p className="text-dark-500">Sign in to continue</p>
            </div>

            {/* Google Sign In */}
            <button
              type="button"
              onClick={onGoogleClick}
              disabled={isGoogleLoading || isLoading}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-dark-200 rounded-xl hover:border-dark-300 hover:bg-dark-50 transition-all duration-200 font-semibold text-dark-700 disabled:opacity-70 mb-4"
            >
              {isGoogleLoading ? (
                <div className="w-5 h-5 border-2 border-dark-300 border-t-primary-500 rounded-full animate-spin" />
              ) : (
                <>
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Sign in with Google
                </>
              )}
            </button>

            {/* Divider */}
            <div className="relative mb-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-dark-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-dark-400">Or use your email</span>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-dark-700 mb-1">Email</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className={`h-4 w-4 ${errors.email ? 'text-danger' : 'text-dark-400'}`} />
                  </div>
                  <input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`block w-full pl-9 pr-3 py-2.5 border ${
                      errors.email ? 'border-danger' : 'border-dark-200'
                    } rounded-lg text-dark-900 placeholder-dark-400 focus:outline-none focus:border-primary-500 text-sm`}
                    placeholder="you@example.com"
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-xs text-danger">{errors.email}</p>
                )}
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-semibold text-dark-700 mb-1">Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className={`h-4 w-4 ${errors.password ? 'text-danger' : 'text-dark-400'}`} />
                  </div>
                  <input
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    className={`block w-full pl-9 pr-9 py-2.5 border ${
                      errors.password ? 'border-danger' : 'border-dark-200'
                    } rounded-lg text-dark-900 placeholder-dark-400 focus:outline-none focus:border-primary-500 text-sm`}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-dark-400 hover:text-dark-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-xs text-danger">{errors.password}</p>
                )}
              </div>

              {/* Remember & Forgot */}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="rememberMe"
                    checked={rememberMe}
                    onChange={handleChange}
                    className="w-4 h-4 text-primary-600 rounded border-dark-300"
                  />
                  <span className="text-sm text-dark-600">Remember me</span>
                </label>
                <button
                  type="button"
                  onClick={() => switchView('forgot')}
                  className="text-sm text-primary-600 hover:text-primary-700 font-semibold"
                >
                  Forgot password?
                </button>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 bg-primary-600 text-white px-4 py-3 rounded-lg font-semibold hover:bg-primary-700 disabled:opacity-70 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Signing you in...
                  </>
                ) : (
                  <>
                    Sign In
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            {/* Footer */}
            <div className="mt-6 text-center">
              <p className="text-sm text-dark-500">
                Don't have an account?{' '}
                <button
                  type="button"
                  onClick={() => switchView('signup')}
                  className="text-primary-600 hover:text-primary-700 font-semibold"
                >
                  Create one here
                </button>
              </p>
            </div>
          </div>
        );

      case 'forgot':
        return (
          <div>
            {/* Header */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-warning-100 mb-4">
                <Mail className="w-6 h-6 text-warning-600" />
              </div>
              <h2 className="text-2xl font-bold text-dark-900 mb-2">Reset Your Password</h2>
              <p className="text-dark-500">We'll send you a link to reset it</p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-dark-700 mb-1">Email</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className={`h-4 w-4 ${errors.email ? 'text-danger' : 'text-dark-400'}`} />
                  </div>
                  <input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`block w-full pl-9 pr-3 py-2.5 border ${
                      errors.email ? 'border-danger' : 'border-dark-200'
                    } rounded-lg text-dark-900 placeholder-dark-400 focus:outline-none focus:border-primary-500 text-sm`}
                    placeholder="you@example.com"
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-xs text-danger">{errors.email}</p>
                )}
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 bg-primary-600 text-white px-4 py-3 rounded-lg font-semibold hover:bg-primary-700 disabled:opacity-70 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Sending link...
                  </>
                ) : (
                  <>
                    Send Reset Link
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            {/* Footer */}
            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => switchView('signin')}
                className="text-sm text-primary-600 hover:text-primary-700 font-semibold"
              >
                Back to sign in
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {showEmailVerification && (
        <EmailVerificationModal
          isOpen={showEmailVerification}
          onClose={() => setShowEmailVerification(false)}
          email={verificationEmail}
        />
      )}
      
      {!showEmailVerification && (
        <div className="w-full">
          {/* Google Error */}
          {googleError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
              {googleError}
            </div>
          )}
          
          {renderContent()}
        </div>
      )}
    </>
  );
}
